import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminShared.css";
import { API_BASE_URL } from "../config";

const CourseAdminPanel = () => {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState("All");
  const [selectedLocation, setSelectedLocation] = useState("All");
  const [courses, setCourses] = useState([]);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editData, setEditData] = useState(null);

  const fetchEnrollments = async (course = "All", location = "All") => {
    const adminInfo = JSON.parse(localStorage.getItem("adminInfo") || "{}");
    const token = adminInfo?.token;
    let query = [];
    if (course !== "All") query.push(`course=${course}`);
    if (location !== "All") query.push(`location=${location}`);
    const queryString = query.length ? `?${query.join("&")}` : "";
    const response = await fetch(
      `${API_BASE_URL}/api/enrollments${queryString}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (!response.ok) throw new Error(`Fetch failed`);
    const data = await response.json();
    return Array.isArray(data)
      ? data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      : [];
  };

  const fetchCourses = async () => {
    const token = JSON.parse(localStorage.getItem("adminInfo") || "{}")?.token;
    const response = await fetch(`${API_BASE_URL}/api/enrollments/courses`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error(`Courses failed`);
    return response.json();
  };

  const loadEnrollments = useCallback(
    async (course = selectedCourse, location = selectedLocation) => {
      try {
        setLoading(true);
        const data = await fetchEnrollments(course, location);
        setEnrollments(data);
      } catch (err) {
        console.error(err);
        setEnrollments([]);
      } finally {
        setLoading(false);
      }
    },
    [selectedCourse, selectedLocation],
  );

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("adminInfo");
      if (!token) return navigate("/admin");
      try {
        const availableCourses = await fetchCourses();
        setCourses(["All", ...availableCourses]);
        await loadEnrollments();
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, [loadEnrollments, navigate]);

  const handleDelete = async (id) => {
    if (!window.confirm("Purge registration entry?")) return;
    try {
      setDeleting(id);
      const token = JSON.parse(
        localStorage.getItem("adminInfo") || "{}",
      )?.token;
      await fetch(`${API_BASE_URL}/api/enrollments/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnrollments((prev) => prev.filter((e) => e._id !== id));
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(null);
    }
  };

  const updateEnrollment = async () => {
    try {
      const token = JSON.parse(
        localStorage.getItem("adminInfo") || "{}",
      )?.token;
      const res = await fetch(
        `${API_BASE_URL}/api/enrollments/update/${editData._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(editData),
        },
      );
      if (!res.ok) throw new Error("Sync failed");
      alert("✅ Record Synchronized.");
      setShowEditPopup(false);
      loadEnrollments();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleEdit = (data) => {
    setEditData({ ...data });
    setShowEditPopup(true);
  };

  const handleStatusChange = async (id, status) => {
    try {
      const token = JSON.parse(
        localStorage.getItem("adminInfo") || "{}",
      )?.token;
      await fetch(`${API_BASE_URL}/api/enrollments/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      setEnrollments((prev) =>
        prev.map((e) => (e._id === id ? { ...e, status } : e)),
      );
    } catch (err) {
      console.error(err);
    }
  };

  if (loading)
    return (
      <div className="admin-list-container">
        <p style={{ padding: "100px", textAlign: "center" }}>
          ⏳ Synchronizing Global Registrations...
        </p>
      </div>
    );

  return (
    <div className="admin-list-container">
      <div className="admin-list-content">
        <div className="page-title-section">
          <h1>Enrollment Ledger</h1>
          <p>Monitor and manage global student registrations.</p>
        </div>
        <div className="admin-info-bar">
          <div className="admin-user-tag">
            <div className="dot" style={{ background: "#6366f1" }}></div>
            Operator: Admissions Registrar Node
          </div>
          <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
            <div
              className="filter-group-elite"
              style={{ display: "flex", gap: "10px" }}
            >
              <select
                value={selectedCourse}
                onChange={(e) => {
                  setSelectedCourse(e.target.value);
                  loadEnrollments(e.target.value, selectedLocation);
                }}
                className="elite-select"
                style={{ minWidth: "180px" }}
              >
                <option disabled>Select Curriculum</option>
                {courses.map((c, i) => (
                  <option key={i} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <select
                value={selectedLocation}
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                  loadEnrollments(selectedCourse, e.target.value);
                }}
                className="elite-select"
                style={{ minWidth: "150px" }}
              >
                <option disabled>Select Location</option>
                <option value="All">All Regions</option>
                <option value="Namakkal">Namakkal Branch</option>
                <option value="Bangalore">Bangalore HQ</option>
              </select>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {[
                { label: "NEW", color: "#6366f1", bg: "#eef2ff", key: "New" },
                {
                  label: "CONTACTED",
                  color: "#0891b2",
                  bg: "#ecfeff",
                  key: "Contacted",
                },
                {
                  label: "PENDING",
                  color: "#d97706",
                  bg: "#fffbeb",
                  key: "Pending",
                },
                {
                  label: "CLOSED",
                  color: "#059669",
                  bg: "#ecfdf5",
                  key: "Closed",
                },
              ].map(({ label, color, bg, key }) => (
                <div
                  key={key}
                  className="admin-status-badge"
                  style={{
                    background: bg,
                    color,
                    fontSize: "0.75rem",
                    fontWeight: 800,
                  }}
                >
                  {
                    enrollments.filter((e) => (e.status || "New") === key)
                      .length
                  }{" "}
                  {label}
                </div>
              ))}
            </div>
            <button
              className="btn-primary-elite"
              onClick={() => loadEnrollments()}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                background: "#6366f1",
                border: "none",
                color: "#fff",
                padding: "10px 20px",
                borderRadius: "100px",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              <span>🔄</span> Sync Ledger
            </button>
          </div>
        </div>

        <div className="admin-table-wrapper">
          <table className="elite-table">
            <thead>
              <tr>
                <th>Candidate Profile</th>
                <th>Academic Stream</th>
                <th>Regional Node</th>
                <th>Temporal Stamp</th>
                <th>Status</th>
                <th>Operations</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    style={{
                      textAlign: "center",
                      padding: "100px",
                      color: "#94a3b8",
                    }}
                  >
                    No registration fragments detected in the current buffer.
                  </td>
                </tr>
              ) : (
                enrollments.map((item) => (
                  <tr
                    key={item._id}
                    style={{
                      background:
                        (item.status || "New") === "New"
                          ? "#f5f3ff"
                          : "transparent",
                      transition: "background 0.3s ease",
                    }}
                  >
                    <td>
                      <div style={{ fontWeight: 700 }}>{item.name}</div>
                      <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                        {item.email}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                        {item.phone}
                      </div>
                    </td>
                    <td>
                      <span
                        className="status-tag status-progress"
                        style={{ width: "fit-content" }}
                      >
                        {item.course}
                      </span>
                      <div
                        style={{
                          fontSize: "0.75rem",
                          marginTop: "6px",
                          color: "#94a3b8",
                          maxWidth: "180px",
                        }}
                      >
                        {item.address}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 700, color: "#6366f1" }}>
                        {item.location?.toUpperCase() || "REMOTE"}
                      </div>
                      <div style={{ fontSize: "0.7rem", color: "#94a3b8" }}>
                        REGISTRATION-POINT
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {new Date(item.createdAt).toLocaleDateString()}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#94a3b8" }}>
                        {new Date(item.createdAt).toLocaleTimeString()}
                      </div>
                    </td>
                    <td>
                      <select
                        className={`status-tag status-${(item.status || "New").toLowerCase()}`}
                        value={item.status || "New"}
                        onChange={(e) =>
                          handleStatusChange(item._id, e.target.value)
                        }
                        style={{
                          border: "none",
                          cursor: "pointer",
                          outline: "none",
                        }}
                      >
                        {["New", "Contacted", "Pending", "Closed"].map((s) => (
                          <option key={s} value={s}>
                            {s.toUpperCase()}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          className="action-btn-elite btn-update"
                          onClick={() => handleEdit(item)}
                        >
                          <span>✏️</span>
                        </button>
                        <button
                          className="action-btn-elite btn-delete"
                          onClick={() => handleDelete(item._id)}
                          disabled={deleting === item._id}
                        >
                          {deleting === item._id ? "..." : <span>🗑️</span>}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showEditPopup && editData && (
        <div
          className="admin-form-overlay"
          onClick={() => setShowEditPopup(false)}
        >
          <div
            className="admin-side-panel"
            style={{ padding: "40px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              style={{
                fontWeight: 800,
                color: "#0f172a",
                marginBottom: "10px",
              }}
            >
              Sync Candidate Metrics
            </h2>

            <div
              className="admin-form-elite side-form"
              style={{ marginTop: "10px" }}
            >
              <label>
                Candidate Profile Name
                <input
                  value={editData.name}
                  onChange={(e) =>
                    setEditData({ ...editData, name: e.target.value })
                  }
                />
              </label>
              <label>
                Digital Communication
                <input
                  value={editData.email}
                  onChange={(e) =>
                    setEditData({ ...editData, email: e.target.value })
                  }
                />
              </label>
              <label>
                Cellular Node
                <input
                  value={editData.phone}
                  onChange={(e) =>
                    setEditData({ ...editData, phone: e.target.value })
                  }
                />
              </label>
              <label>
                Curriculum Stream
                <input
                  value={editData.course}
                  onChange={(e) =>
                    setEditData({ ...editData, course: e.target.value })
                  }
                />
              </label>
              <label>
                Operational Hub
                <select
                  value={editData.location || "Namakkal"}
                  onChange={(e) =>
                    setEditData({ ...editData, location: e.target.value })
                  }
                >
                  <option value="Namakkal">Namakkal Branch</option>
                  <option value="Bangalore">Bangalore HQ</option>
                </select>
              </label>
              <label>
                Physical Domicile Fragment
                <textarea
                  value={editData.address}
                  onChange={(e) =>
                    setEditData({ ...editData, address: e.target.value })
                  }
                  style={{ height: "80px" }}
                />
              </label>
            </div>

            <div
              style={{
                display: "flex",
                gap: "15px",
                marginTop: "auto",
                paddingTop: "30px",
              }}
            >
              <button
                className="btn-primary-elite"
                style={{ flex: 1, background: "#6366f1" }}
                onClick={updateEnrollment}
              >
                Confirm Sync
              </button>
              <button
                className="btn-update"
                style={{
                  flex: 1,
                  background: "#f1f5f9",
                  border: "none",
                  color: "#64748b",
                }}
                onClick={() => setShowEditPopup(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseAdminPanel;
